/**
 * Animated game-show stage background. Pure CSS (radial sweep + slow pan).
 * Mounted as the FIRST child of stage so it renders behind everything else.
 *
 * Adapted from WhatIs: same subtle stage feel — conic sweep + soft glows +
 * star field — recoloured against the theme primary/accent CSS vars.
 */

const ID = 'an-animated-bg';

export interface AnimatedBgHandle {
  el: HTMLDivElement;
  setVisible: (v: boolean) => void;
  dispose: () => void;
}

export function createAnimatedBg(mount: HTMLElement): AnimatedBgHandle {
  if (!document.getElementById(`${ID}-style`)) {
    const style = document.createElement('style');
    style.id = `${ID}-style`;
    style.textContent = `
      @keyframes an-bg-sweep {
        0%   { transform: translate(-10%, -10%) rotate(0deg); }
        50%  { transform: translate(10%, 10%)  rotate(180deg); }
        100% { transform: translate(-10%, -10%) rotate(360deg); }
      }
      @keyframes an-bg-pulse {
        0%, 100% { opacity: 0.55; }
        50%      { opacity: 0.85; }
      }
      .an-bg-root {
        position: absolute;
        inset: 0;
        overflow: hidden;
        z-index: 0;
        pointer-events: none;
        background:
          radial-gradient(ellipse at 50% 30%, rgba(6,12,233,0.55) 0%, rgba(3,4,40,0.95) 60%, #02021a 100%);
      }
      .an-bg-sweep {
        position: absolute;
        width: 200%;
        height: 200%;
        left: -50%;
        top: -50%;
        background:
          conic-gradient(from 0deg,
            rgba(255,196,61,0.0)  0%,
            rgba(255,196,61,0.10) 18%,
            rgba(0,191,166,0.0)   35%,
            rgba(0,191,166,0.08)  55%,
            rgba(255,196,61,0.0)  72%,
            rgba(255,196,61,0.0)  100%);
        animation: an-bg-sweep 32s linear infinite;
        opacity: 0.7;
        mix-blend-mode: screen;
      }
      .an-bg-glow {
        position: absolute;
        inset: 0;
        background:
          radial-gradient(circle at 30% 70%, rgba(255,196,61,0.18) 0%, transparent 40%),
          radial-gradient(circle at 70% 30%, rgba(0,191,166,0.18) 0%, transparent 40%);
        animation: an-bg-pulse 6s ease-in-out infinite;
        mix-blend-mode: screen;
      }
      .an-bg-stars {
        position: absolute;
        inset: 0;
        background-image:
          radial-gradient(2px 2px at 20% 30%, rgba(255,255,255,0.6), transparent),
          radial-gradient(1px 1px at 80% 70%, rgba(255,255,255,0.5), transparent),
          radial-gradient(2px 2px at 60% 20%, rgba(255,196,61,0.6), transparent),
          radial-gradient(1px 1px at 35% 80%, rgba(0,191,166,0.6), transparent),
          radial-gradient(1px 1px at 75% 50%, rgba(255,255,255,0.4), transparent),
          radial-gradient(1px 1px at 10% 60%, rgba(255,255,255,0.4), transparent),
          radial-gradient(2px 2px at 90% 15%, rgba(255,196,61,0.5), transparent);
        opacity: 0.6;
      }
    `;
    document.head.appendChild(style);
  }

  const el = document.createElement('div');
  el.id = ID;
  el.className = 'an-bg-root';
  el.setAttribute('data-overlay', 'animated-bg');
  el.innerHTML = `
    <div class="an-bg-sweep"></div>
    <div class="an-bg-glow"></div>
    <div class="an-bg-stars"></div>
  `;
  mount.insertBefore(el, mount.firstChild);

  return {
    el,
    setVisible(v: boolean) {
      el.style.display = v ? 'block' : 'none';
    },
    dispose() {
      el.remove();
    },
  };
}
